import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useRevision, useRevisions } from "../../lib/articles";
import { ensureDoc } from "../editor/utils";
import { BlockRenderer } from "../editor/BlockRenderer";
import { articleEditorConfig } from "../editor/articleConfig";
import type { BlockDoc } from "../editor/types";

/**
 * Slide-in drawer that lists previous revisions of an article and lets the
 * author preview a chosen revision in a read-only block view. Calling
 * `onRestore(versionNumber, doc)` is delegated back to the parent form, which
 * dispatches a PATCH that writes the chosen content as the new tip.
 */
export function RevisionDrawer({
  articleId,
  open,
  onClose,
  onRestore,
}: {
  articleId: number | null;
  open: boolean;
  onClose: () => void;
  onRestore: (versionNumber: number, doc: BlockDoc, html: string) => void;
}) {
  const list = useRevisions(open ? articleId : null);
  const revisions = list.data?.revisions ?? [];
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    if (open && selected === null && revisions.length > 0) {
      setSelected(revisions[0]!.versionNumber);
    }
  }, [open, revisions, selected]);

  // Reset selection when the drawer closes.
  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const detail = useRevision(open ? articleId : null, selected);
  const rev = detail.data?.revision;
  const doc: BlockDoc = rev ? ensureDoc(rev.contentJson) : { version: 1, blocks: [] };

  return (
    <div className={`rev-drawer ${open ? "is-open" : ""}`} aria-hidden={!open}>
      <div className="rev-drawer-overlay" onClick={onClose} />
      <aside className="rev-drawer-panel" role="dialog" aria-label="Revision history">
        <header className="rev-drawer-header">
          <p className="page-eyebrow">History</p>
          <h2 className="page-title">Past versions</h2>
          <button type="button" onClick={onClose} className="rev-drawer-close" aria-label="Close">
            ✕
          </button>
        </header>

        <div className="rev-drawer-body">
          <ul className="rev-list">
            {list.isLoading && <li className="rev-list-empty">Loading…</li>}
            {!list.isLoading && revisions.length === 0 && (
              <li className="rev-list-empty">No revisions yet.</li>
            )}
            {revisions.map((r) => (
              <li key={r.versionNumber}>
                <button
                  type="button"
                  className={`rev-item ${selected === r.versionNumber ? "is-active" : ""}`}
                  onClick={() => setSelected(r.versionNumber)}
                >
                  <span className="rev-item-v">v{r.versionNumber}</span>
                  <span className="rev-item-date">
                    {format(new Date(r.createdAt), "d LLL yyyy · HH:mm")}
                  </span>
                  <span className="rev-item-title">{r.title}</span>
                  {r.note && <span className="rev-item-note">{r.note}</span>}
                </button>
              </li>
            ))}
          </ul>

          <div className="rev-preview">
            {!rev ? (
              <p className="rev-preview-empty">Select a version to preview.</p>
            ) : (
              <>
                <div className="rev-preview-meta">
                  <span>v{rev.versionNumber}</span>
                  <span className="mx-2">·</span>
                  <span>{format(new Date(rev.createdAt), "d LLL yyyy · HH:mm")}</span>
                </div>
                <h3 className="rev-preview-title">{rev.title}</h3>
                <BlockRenderer
                  doc={doc}
                  config={articleEditorConfig}
                  htmlFallback={rev.contentHtml}
                  className="rev-preview-body"
                />
              </>
            )}
          </div>
        </div>

        {rev && (
          <footer className="rev-drawer-footer">
            <button type="button" className="btn-app-ghost" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn-app"
              onClick={() => {
                if (
                  window.confirm(
                    `Restore v${rev.versionNumber}? The current draft state will be replaced. Save afterwards to commit.`,
                  )
                ) {
                  onRestore(rev.versionNumber, doc, rev.contentHtml);
                }
              }}
            >
              Restore v{rev.versionNumber}
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}
